import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gavel, Clock, Loader2, Filter, ChevronRight, Flame, Package, Video } from "lucide-react";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const BrowseAuctions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const status = searchParams.get("status") || "active";

  useEffect(() => {
    fetchAuctions();
    // Refresh every 30 seconds for countdown updates
    const interval = setInterval(fetchAuctions, 30000);
    return () => clearInterval(interval);
  }, [status]);

  const fetchAuctions = async () => {
    try {
      const response = await api.get(`/auctions?status=${status}`);
      setAuctions(response.data.auctions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      logger.error("Failed to fetch auctions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return "Ended";
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const isEndingSoon = (seconds) => seconds > 0 && seconds < 3600; // Less than 1 hour

  const AuctionCard = ({ auction }) => (
    <Link
      to={`/auctions/${auction.auction_id}`}
      className="glass-card overflow-hidden hover:border-[#D4AF37]/50 transition-colors block"
      data-testid={`auction-card-${auction.auction_id}`}
    >
      {/* Image */}
      <div className="relative h-48 bg-[#18181B]">
        {auction.listing_photo ? (
          <img 
            src={auction.listing_photo} 
            alt={auction.listing_title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl">🐴</div>
        )}
        
        {/* Timer Badge */}
        <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${
          isEndingSoon(auction.time_remaining_seconds) 
            ? "bg-[#EF4444] text-white animate-pulse" 
            : "bg-black/70 text-white"
        }`}>
          {isEndingSoon(auction.time_remaining_seconds) && <Flame className="w-4 h-4" />}
          <Clock className="w-4 h-4" />
          {formatTimeRemaining(auction.time_remaining_seconds)}
        </div>

        {/* Buy Now Badge */}
        {auction.buy_now_price && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-[#22C55E] text-white text-xs rounded-full">
            Buy Now Available
          </div>
        )}

        {/* Lot / Video Badges */}
        <div className="absolute bottom-3 left-3 flex gap-1.5">
          {auction.is_lot && (
            <div className="px-2 py-1 bg-[#D4AF37]/90 text-black text-xs rounded-full flex items-center gap-1" data-testid="lot-badge">
              <Package className="w-3 h-3" /> Lot of {auction.lot_count || "2+"}
            </div>
          )}
          {auction.video_url && (
            <div className="px-2 py-1 bg-blue-500/90 text-white text-xs rounded-full flex items-center gap-1" data-testid="video-badge">
              <Video className="w-3 h-3" /> Video
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] rounded text-xs capitalize">
            {auction.species}
          </span>
          {auction.breed && (
            <span className="text-xs text-[#71717A]">{auction.breed}</span>
          )}
        </div>

        <h3 className="text-lg text-[#EDEDED] mb-3 truncate">{auction.listing_title}</h3>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[#71717A]">Current Bid</p>
            <p className="text-xl text-[#D4AF37] font-semibold">
              ${(auction.current_bid || auction.starting_bid).toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#71717A]">{auction.bid_count || 0} bids</p>
            {auction.buy_now_price && (
              <p className="text-sm text-[#22C55E]">
                Buy: ${auction.buy_now_price.toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="pt-20 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm mb-4">
            <Gavel className="w-4 h-4" /> Live Auctions
          </div>
          <h1 className="text-3xl md:text-4xl text-[#EDEDED] mb-3">Auction Ring</h1>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            Bid on premium livestock and animals. Watch the countdown - highest bid at the gavel wins.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setSearchParams({ status: "active" })}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              status === "active" 
                ? "bg-[#D4AF37] text-black" 
                : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
            }`}
          >
            Live Auctions
          </button>
          <button
            onClick={() => setSearchParams({ status: "ended" })}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              status === "ended" 
                ? "bg-[#D4AF37] text-black" 
                : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"
            }`}
          >
            Recently Ended
          </button>
        </div>

        {/* Results */}
        <div className="mb-4">
          <p className="text-sm text-[#71717A]">
            {loading ? "Loading..." : `${total} auction${total !== 1 ? "s" : ""}`}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Gavel className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h3 className="text-xl text-[#EDEDED] mb-2">No Active Auctions</h3>
            <p className="text-[#A1A1AA] mb-6">
              Check back soon for new auction listings
            </p>
            <Link to="/browse" className="btn-gold inline-flex items-center gap-2">
              Browse Listings <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {auctions.map(auction => (
              <AuctionCard key={auction.auction_id} auction={auction} />
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-12 glass-card p-6 border-[#D4AF37]/20">
          <h3 className="text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
            <Gavel className="w-5 h-5 text-[#D4AF37]" /> How Auctions Work
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div>
              <p className="text-[#D4AF37] font-medium mb-1">Place Your Bid</p>
              <p className="text-[#A1A1AA]">
                Sign in, add a payment method, then bid at or above the minimum. Outbid? You'll be notified.
              </p>
            </div>
            <div>
              <p className="text-[#D4AF37] font-medium mb-1">Watch the Clock</p>
              <p className="text-[#A1A1AA]">
                Auctions extend 2 minutes if bid placed in final moments. No sniping!
              </p>
            </div>
            <div>
              <p className="text-[#D4AF37] font-medium mb-1">Win & Pay</p>
              <p className="text-[#A1A1AA]">
                The winner pays the final bid plus a 3% payment processing fee. Breedling’s separate platform commission is deducted from the seller according to their plan.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowseAuctions;
